import { isNullOrUndefined } from '@utils/types'
import { TieredMenu } from 'primereact/tieredmenu'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Display a Pagination for a list of items.
 *
 * @component
 * @param {Object} props
 * @param {number} props.count - Total number of items across all pages.
 * @param {number} [props.offset] - Current offset (zero-based index of the first item on the page).
 * @param {number} [props.limit] - Number of items per page.
 * @param {Function} [props.setOffset] - Callback to update the offset.
 * @param {Function} [props.setLimit] - Callback to update the limit.
 * @returns {JSX.Element}
 */
const Pagination = ({ count, offset = null, limit = null, setOffset = null, setLimit = null }) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(0)
  const [pages, setPages] = useState(0)
  const itemPerPageSelector = useRef(null)
  const visibleItems = 9
  const availableOptions = [5, 10, 50, 100, 1000]
  const hasOffset = !isNullOrUndefined(offset)
  const displayPagination = !!count && hasOffset && !!limit && !!setOffset && !!setLimit

  /**
   * Mapping options for items per page selector.
   *
   * @type {{label: *, disabled: boolean, command: function(*): void}[]}
   */
  const options = availableOptions.map(option => ({
    label: option,
    disabled: option === limit,
    command: e => {
      setOffset(0)
      setLimit(parseInt(e.item.label))
    }
  }))

  /**
   * Page numbers component.
   *
   * @component
   * @returns {JSX.Element}
   */
  const PageNumbers = () => {
    const minStart = Math.max(0, Math.ceil(currentPage - Math.floor(visibleItems / 2)))
    const maxStart = Math.max(0, pages - visibleItems)
    const start = Math.min(minStart, maxStart)
    const end = Math.min(start + visibleItems, pages)
    const pageItems = Array.from({ length: end - start }, (_, index) => start + index)

    return (
      <>
        <div
          className={`previous-next-page ${currentPage > 0 ? 'enabled' : 'disabled'}`}
          onClick={() => setOffset((currentPage - 1) * limit)}
        >
          <i className="fa-solid fa-chevron-left" />
        </div>

        {pageItems.map(item => (
          <div
            key={item}
            className={item === currentPage ? 'current-page-number' : 'page-number'}
            onClick={() => setOffset(item * limit)}
          >
            {item + 1}
          </div>
        ))}

        <div
          className={`previous-next-page ${currentPage < pages - 1 ? 'enabled' : 'disabled'}`}
          onClick={() => setOffset((currentPage + 1) * limit)}
        >
          <i className="fa-solid fa-chevron-right" />
        </div>
      </>
    )
  }

  useEffect(() => {
    setCurrentPage(Math.ceil(offset / limit))
    window.scrollTo(0, 0)
  }, [offset, limit])

  useEffect(() => {
    count && setPages(Math.ceil(count / limit))
  }, [count, limit])

  return (
    displayPagination && (
      <div className="pagination">
        {!!pages && pages > 1 && (
          <div className="page-numbers">
            <PageNumbers />
          </div>
        )}
        <div className="limit-selection">
          <button
            type="button"
            onClick={e => itemPerPageSelector.current.toggle(e)}
          >
            {t('common.items_per_page', { limit })}
          </button>
          <TieredMenu
            className="pagination-limit-list"
            model={options}
            ref={itemPerPageSelector}
            popup
          />
        </div>
      </div>
    )
  )
}

export default Pagination
